import React from 'react'
import clsx from 'clsx'
import { Header4, Pill, PSmallFaded, PSmallStrong, PMedium } from './Type'
import { Link } from 'react-router-dom'

// === Component Props Interfaces ===

interface CardProps extends React.HTMLAttributes<HTMLElement> {
  children: React.ReactNode
  className?: string
  as?: React.ElementType
}

interface SingleStatCardProps {
  stat: string | number
  label: string
}

// Icon components (e.g., Heroicons) are plain React components that accept
// standard SVG props.  Using this generic type keeps compatibility with
// both Heroicons and any inline SVG components we create locally.
type IconComponent = React.ComponentType<React.SVGProps<SVGSVGElement>>

interface HorizontalCardWithIconProps {
  title: string
  subtitle: string
  icon: IconComponent
  bgcolor: string
  onClick?: () => void
}

interface VerticalCardWithIconProps {
  stat?: number | string
  title: string
  subtitle: string
  icon: IconComponent
  bgcolor: string
  onClick?: () => void
  disabled?: boolean
}

interface ProgressBarProps {
  percent?: number
  height?: number
  bgColor?: string
  barBgColor?: string
  label?: string | null
}

interface UserData {
  // Support both naming conventions with optional properties
  // Original properties
  id?: string
  name?: string
  avatar?: string
  // New properties from JiraUser
  accountId?: string
  displayName?: string
  avatarUrl?: string
  // Other required properties
  role: string
  resolvedPoints: number
  startPoints: number
  resolvedBugs: number
  startBugs: number
}

interface UserProgressCardProps {
  user: UserData
  link?: string | null
}

/**
 * Generic Card component for consistent card styling.
 * Usage: <Card className="p-4">...</Card>
 * Supports custom wrapper via `as` prop (e.g., as={Link} to="/path").
 * Forwards refs to the underlying component.
 */
export const Card = React.forwardRef<HTMLElement, CardProps>(
  ({ children, className = '', as: Component = 'div', ...props }, ref) => (
    <Component
      ref={ref}
      className={clsx(
        // base styling
        'bg-white rounded-lg shadow',
        // Only add overflow-hidden if not explicitly overridden
        !className.includes('overflow-') && 'overflow-hidden',
        // unified hover interaction
        'transition-all duration-150 hover:shadow-lg hover:ring-2 hover:ring-indigo-500/50',
        // allow consumers to extend / override
        className
      )}
      {...props}
    >
      {children}
    </Component>
  )
)

// Give the forward-ref component an explicit name for React DevTools
Card.displayName = 'Card'

export const SingleStatCard: React.FC<SingleStatCardProps> = ({ stat, label }) => {
  return (
    <Card className='px-4 py-5 sm:p-6 lg:py-3'>
      <div className='truncate text-sm font-medium text-gray-500'>{label}</div>
      <div className='mt-1 text-3xl font-semibold tracking-tight text-gray-900'>
        {stat}
      </div>
    </Card>
  )
}

export const HorizontalCardWithIcon: React.FC<HorizontalCardWithIconProps> = ({
  title,
  subtitle,
  icon,
  bgcolor,
  onClick
}) => {
  const CardIcon = icon
  return (
    <div
      onClick={onClick || undefined}
      className={clsx(
        onClick && 'hover:bg-slate-50',
        'col-span-1 flex cursor-pointer rounded-lg bg-white text-left shadow'
      )}
    >
      <div
        className={clsx(
          bgcolor,
          'flex w-24 flex-shrink-0 items-center justify-center rounded-l-md'
        )}
      >
        <CardIcon className='h-12 w-12 text-white' aria-hidden='true' />
      </div>
      <div className='flex flex-1 items-center justify-between truncate rounded-r-md'>
        <div className='overflow-hidden px-4 py-5 pl-8 sm:p-6 lg:py-3'>
          <dt className='truncate text-sm font-medium text-gray-500'>
            {subtitle}
          </dt>
          <dd className='mt-1 text-xl font-semibold tracking-tight text-gray-900'>
            {title}
          </dd>
        </div>
      </div>
    </div>
  )
}

// Vertical card layout: icon on top, title and subtitle below
export const VerticalCardWithIcon: React.FC<VerticalCardWithIconProps> = ({
  stat, title, subtitle, icon, bgcolor, onClick, disabled = false
}) => {
  const CardIcon = icon
  const headerStat = stat !== undefined ? String(stat) : title.split(' ')[0]
  const bodyTitle = stat !== undefined ? title : (title && title.includes(' ') ? title : null)
  return (
    <div
      onClick={!disabled && onClick ? onClick : undefined}
      className={clsx(
        disabled
          ? 'opacity-60 cursor-default pointer-events-none' // fade + block mouse
          : onClick && 'cursor-pointer',
        'col-span-1 flex flex-col rounded-lg bg-white text-center shadow',
        // unified hover / focus interaction
        'transition-all duration-150 hover:shadow-lg hover:ring-2 hover:ring-indigo-500/50'
      )}
      tabIndex={disabled ? -1 : 0}
      aria-disabled={disabled}
    >
      <div
        className={clsx(
          bgcolor,
          disabled ? 'text-gray-400 bg-slate-300' : '',
          'w-full p-4 rounded-t-lg flex items-center justify-center space-x-2'
        )}
      >
        <CardIcon className={clsx('h-8 w-8', disabled ? 'text-gray-500' : 'text-white')} aria-hidden='true' />
        <span className={clsx('text-3xl font-semibold', disabled ? 'text-gray-500' : 'text-white')}>{headerStat}</span>
      </div>
      <div className='pb-4 flex flex-col items-center flex-1 space-y-1'>
        {bodyTitle && <PMedium text={bodyTitle} className={clsx('pb-0', disabled ? 'text-gray-400' : '')} />}
        <PSmallFaded text={subtitle} className={disabled ? 'text-gray-300' : ''} />
      </div>
    </div>
  )
}

export const ProgressBar: React.FC<ProgressBarProps> = ({
  percent = 0,
  height = 1,
  bgColor = 'bg-slate-50',
  barBgColor = 'bg-slate-500',
  label = null
}) => {
  const width = Number(percent).toLocaleString(undefined, {
    style: 'percent',
    minimumFractionDigits: 0
  })

  return (
    <div className='mb-2'>
      {label && (
        <div className='mb-2'>
          <PSmallFaded text={label} />
        </div>
      )}
      <div className={clsx('w-full', `h-${height}`, bgColor)}>
        <div
          className={clsx(`h-${height}`, barBgColor)}
          style={{ width: width }}
        ></div>
      </div>
    </div>
  )
}

export const UserProgressCard: React.FC<UserProgressCardProps> = ({ user, link = null }) => {
  const pointsDeliveredPercent = user.resolvedPoints
    ? user.resolvedPoints / user.startPoints
    : 0
  const bugsDeliveredPercent = user.resolvedBugs
    ? user.resolvedBugs / user.startBugs
    : 0

  // Get user name with fallback
  const userName = user.displayName || user.name || 'Unknown User'
  // Get user avatar with fallback
  const userAvatar = user.avatarUrl || user.avatar || ''

  let progressColor = 'red'

  if (pointsDeliveredPercent > 0.99) {
    progressColor = 'green'
  } else if (pointsDeliveredPercent > 0.85) {
    progressColor = 'yellow'
  } else if (pointsDeliveredPercent > 0.5) {
    progressColor = 'orange'
  }

  const Content: React.FC = () => {
    return (
      <div className='relative flex flex-1 flex-col p-8'>
        <div className='absolute top-0 right-0 mt-4 mr-4'>
          <Pill
            color={progressColor}
            textcolor={progressColor}
            text={Number(pointsDeliveredPercent).toLocaleString(undefined, {
              style: 'percent',
              minimumFractionDigits: 0
            })}
          />
          { }
        </div>
        <img
          className='mx-auto h-12 w-12 flex-shrink-0 rounded-full'
          src={userAvatar}
          alt=''
        />
        <Header4 title={userName} />
        <div className='mt-1'>
          <Pill color={'slate'} text={user.role} />
        </div>

        <div className='mt-4'>
          {pointsDeliveredPercent > 1 ? (
            <ProgressBar
              percent={pointsDeliveredPercent % 1}
              bgColor={'bg-sky-600'}
              barBgColor={'bg-sky-900'}
            />
          ) : (
            <ProgressBar
              percent={pointsDeliveredPercent}
              bgColor={'bg-sky-100'}
              barBgColor={'bg-sky-600'}
            />
          )}
        </div>

        <div className='mt-2'>
          {bugsDeliveredPercent > 1 ? (
            <ProgressBar
              percent={bugsDeliveredPercent % 1}
              bgColor={'bg-red-600'}
              barBgColor={'bg-red-900'}
            />
          ) : (
            <ProgressBar
              percent={bugsDeliveredPercent}
              bgColor={'bg-red-100'}
              barBgColor={'bg-red-600'}
            />
          )}
        </div>

        <div className='mt-4 grid grid-cols-2'>
          {user.resolvedPoints > user.startPoints ? (
            <PSmallStrong
              text={`Points: ${user.resolvedPoints} / ${user.startPoints}`}
            />
          ) : (
            <PSmallFaded
              text={`Points: ${user.resolvedPoints} / ${user.startPoints}`}
            />
          )}
          {user.resolvedBugs > user.startBugs ? (
            <PSmallStrong
              text={`Bugs: ${user.resolvedBugs} / ${user.startBugs}`}
            />
          ) : (
            <PSmallFaded
              text={`Bugs: ${user.resolvedBugs} / ${user.startBugs}`}
            />
          )}
        </div>
      </div>
    )
  }

  if (link) {
    return (
      <Link to={link}>
        <div
          className='col-span-1 flex flex-col divide-y divide-gray-200 rounded-lg bg-white text-center shadow
                     transition-all duration-150 hover:shadow-lg hover:ring-2 hover:ring-indigo-500/50'>
          <Content />
        </div>
      </Link>
    )
  } else {
    return (
      <div
        className='col-span-1 flex flex-col divide-y divide-gray-200 rounded-lg bg-white text-center shadow
                   transition-all duration-150 hover:shadow-lg hover:ring-2 hover:ring-indigo-500/50'
      >
        <Content />
      </div>
    )
  }
}
